/**
 * Robust fetch wrapper for ALIA API calls.
 *
 * Why this exists:
 * - iOS Safari and some Android browsers fail cross-domain `credentials:"include"`
 *   cookies in production, producing the cryptic "Load failed" TypeError.
 * - We always send the Bearer token from localStorage (set at login/register),
 *   which works identically across mobile and desktop without depending on
 *   third-party cookie policies.
 * - We keep `credentials:"include"` for backward compatibility (same-origin
 *   preview) but the Bearer header is what makes things work in production.
 *
 * Also provides:
 * - 20s timeout via AbortController (mobile networks can hang silently)
 * - Friendly errors that surface a user-readable message
 */

const DEFAULT_TIMEOUT_MS = 20000;

export const getStoredToken = () => {
  try {
    return localStorage.getItem("alia-token") || sessionStorage.getItem("alia-token") || "";
  } catch {
    return "";
  }
};

export const setStoredToken = (token) => {
  try {
    if (token) localStorage.setItem("alia-token", token);
    else localStorage.removeItem("alia-token");
  } catch { /* private mode */ }
};

export const apiFetch = async (url, options = {}) => {
  const { timeout = DEFAULT_TIMEOUT_MS, headers = {}, ...rest } = options;

  const token = getStoredToken();
  const finalHeaders = { ...headers };
  if (token && !finalHeaders.Authorization) {
    finalHeaders.Authorization = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      credentials: "include",
      ...rest,
      headers: finalHeaders,
      signal: controller.signal
    });
    return response;
  } catch (err) {
    if (err.name === "AbortError") {
      const e = new Error("Délai dépassé. Vérifiez votre connexion.");
      e.code = "TIMEOUT";
      throw e;
    }
    // iOS Safari emits "Load failed" / "The network connection was lost."
    const msg = (err && err.message) || "Erreur réseau";
    const friendly = /load failed|network|fetch/i.test(msg)
      ? "Connexion impossible. Vérifiez votre réseau et réessayez."
      : msg;
    const e = new Error(friendly);
    e.code = "NETWORK";
    e.original = err;
    throw e;
  } finally {
    clearTimeout(timer);
  }
};
